import React, { ReactNode, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Icon } from "../icons/icon";
import { useSharedTranslations } from "../../lib";
import { Button } from "../button/button";
import styles from "./file-input.module.scss";

interface IFileInputProps {
    className?: string;
    buttonText: string;
    description?: string | ReactNode;
    initialFiles?: File[];
    multiple?: boolean;
    accept?: string;
    onFilesChange: (files: File[]) => void;
    validate?: boolean;
    triggerValidate?: number;
    clearable?: boolean;
}

export const FileInput: React.FC<IFileInputProps> = React.memo(({
                                                                    className,
                                                                    description,
                                                                    multiple,
                                                                    initialFiles,
                                                                    accept,
                                                                    onFilesChange,
                                                                    buttonText,
                                                                    validate,
                                                                    triggerValidate,
                                                                    clearable = true
                                                                }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();
    const t = useSharedTranslations();

    useEffect(() => {
        if (initialFiles) {
            setFiles(initialFiles);
        }
    }, [initialFiles]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let updatedFiles: File[] = [];
        if (e.target.files && e.target.files.length > 0) {
            updatedFiles = Object.values(e.target.files);
            e.target.value = "";
        }
        setFiles(updatedFiles);
        onFilesChange(updatedFiles);
    };

    const onClearFiles = () => {
        setFiles([]);
        onFilesChange([]);
    };

    const onHandleFileInput = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    useEffect(() => {
        if (triggerValidate && validate) {
            validateValue(files);
        }
    }, [triggerValidate, validate]);

    useEffect(() => {
        if (isError) {
            validateValue(files);
        }
    }, [files]);

    const validateValue = (val: File[] | undefined) => {
        let error = false;
        let message: string | undefined = undefined;
        if (!val || (Array.isArray(val) && val.length === 0)) {
            error = true;
            message = t("requiredFieldError");
        }
        setIsError(error);
        setValidationError(message);
    };

    return (
        <>
            <div className={cn(styles.fileContainer, className)}>
                <div className={styles.innerContainer}>
                    <Button
                        view="legal"
                        className={styles.button}
                        onClick={onHandleFileInput}>
                        <div className={styles.buttonInnerContainer}>
                            <Icon glyph="upload"/>
                            <div>{buttonText}</div>
                        </div>
                    </Button>
                    {files.length > 0 && <div className={styles.fileNamesContainer}>
                        {files.map((file, index) => (
                            <div className={styles.fileName} key={index}>{file.name}</div>
                        ))}
                    </div>}
                    {clearable && files.length > 0 && <div className={styles.closeIconContainer}>
                        <Icon glyph="close" className={styles.closeIcon} onClick={onClearFiles}/>
                    </div>}
                </div>
                {description && <div className={styles.description}>{description}</div>}
                <div className={styles.inputGroup}>
                    <input id="file" type="file" multiple={multiple} accept={accept} onChange={handleFileChange}
                           ref={inputRef}/>
                </div>
            </div>
            {isError && validationError && <div className="Textinput_state_error">
                <div className={cn(styles.validationError, "Textinput-Hint")}>
                    {validationError}
                </div>
            </div>}
        </>
    );
});
